import {
  AnythingCategory,
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
} from 'constants/experience'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { unique, without } from 'lib/array/arrayUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import {
  getOptimizelyFeatureFlagVariation,
  isAllowedToTriggerExperiment,
} from 'lib/optimizely/optimizelyUtils'
import type { ExperienceCoordinates } from 'lib/experiences/experienceUtils'

interface ExperienceSearchStreamingFilters {
  placeId?: string
  from?: string
  to?: string
  priceLte?: number
  priceGte?: number
  minRating?: number
  isGift?: boolean
  targetAudience?: Array<App.ExperienceTargetAudience>
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  bounds?: string
  leExclusive?: boolean
  coordinatesList?: Array<ExperienceCoordinates>
  timeSlots?: Array<Experiences.TimeSlotFilter>
  /** Carousels need limit > 0. Count/existence probes use countOnly instead. */
  limit?: number
  countOnly?: boolean
}

export function shouldUseExperienceSearchStreaming(
  state: App.State,
  filters: ExperienceSearchStreamingFilters,
): boolean {
  const hasCarouselLimit = filters.limit != null && filters.limit > 0
  if (!hasCarouselLimit && !filters.countOnly) return false
  if (
    !filters.placeId
    || filters.from
    || filters.to
    || filters.bounds
    || filters.leExclusive
    || filters.isGift
    || filters.hasFreeCancellation
    || filters.hasInstantConfirmation
    || filters.priceLte != null
    || filters.priceGte != null
    || filters.minRating != null
    || filters.targetAudience?.length
    || filters.coordinatesList?.length
    || filters.timeSlots?.length
  ) return false
  if (!matchLocaleAwarePath(state.router.location.pathname, { path: '/:regionCode/experiences', exact: true })) {
    return false
  }

  return isAllowedToTriggerExperiment(
    true,
    state.utm.content,
    OptimizelyFeatureFlags.experiencesSearchStreaming,
  ) && getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.experiencesSearchStreaming)
}

export function mapSearchStreamingCategories(
  categoryCodes: Array<number>,
  categories: Array<App.ExperienceItemCategory>,
): Array<string> | undefined {
  const codes = without(
    categoryCodes,
    AnythingCategory.id,
    EXP_CATEGORY_LUX_PLUS_ID,
    EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
  )
  if (!codes.length) return []

  const categoryById = new Map(categories.map(category => [Number(category.id), category]))
  const slugs: Array<string> = []

  for (const categoryId of codes) {
    const category = categoryById.get(Number(categoryId))
    if (!category?.name) return
    const slug = experienceCategoryVisionSlug(category.name)

    if (category.level === 'child' && category.parentId) {
      const parentName = categoryById.get(Number(category.parentId))?.name
      const parentSlug = parentName ? experienceCategoryVisionSlug(parentName) : undefined
      if (!parentSlug) return
      slugs.push(`${parentSlug}:${slug}`)
      continue
    }

    slugs.push(slug)
  }

  return unique(slugs)
}

// Match svc-search `slugify` used when indexing experience category tags.
function experienceCategoryVisionSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}
